// Raport z testu odtwarzania nagrania (diagnostyka „Test nagrania”) i opis zdarzeń dziennika odtwarzacza.
// Cel: odróżnić „plik jest ucięty” od „przeglądarka kończy odtwarzanie za wcześnie” od „aplikacja przerwała dźwięk”.

export interface PlaybackProbe {
  id: string;
  status: number;
  bytes: number;
  contentLength: string | null;
  acceptRanges: string | null;
  contentType: string | null;
  /** Długość po pełnym zdekodowaniu pliku (Web Audio) — to, ile faktycznie jest dźwięku w pliku. */
  decodedSec: number | null;
  /** Długość, jaką podaje element <audio> (może być wyliczona z rozmiaru pliku). */
  elementDurationSec: number | null;
  /** Sekunda, na której element <audio> zgłosił koniec odtwarzania (`ended`). */
  endedAtSec: number | null;
  /** Suma odtworzonych fragmentów (`played`). */
  playedSec: number | null;
  /** Odtwarzanie nie zakończyło się w oczekiwanym czasie albo zgłosiło błąd. */
  problem?: string;
}

export interface ProbeLine {
  ok: boolean | null;
  text: string;
}

const TOL = 0.15; // s — różnice mniejsze niż to są szumem pomiaru (koniec ramki mp3, zaokrąglenia)
const f = (n: number) => `${n.toFixed(2).replace('.', ',')} s`;

/** Zamienia wynik pomiaru na czytelne wnioski (kolejno: serwer, plik, przeglądarka). */
export function judgePlayback(p: PlaybackProbe): ProbeLine[] {
  const out: ProbeLine[] = [];
  if (p.status < 200 || p.status >= 300) {
    out.push({ ok: false, text: `Nie udało się pobrać nagrania „${p.id}” (HTTP ${p.status}).` });
    return out;
  }
  out.push({ ok: null, text: `Plik „${p.id}”: ${p.bytes} B, typ ${p.contentType ?? '(brak)'}, Content-Length ${p.contentLength ?? 'BRAK'}, Accept-Ranges ${p.acceptRanges ?? 'BRAK'}.` });
  if (p.contentLength === null) out.push({ ok: false, text: 'Serwer nie podaje Content-Length (wysyła plik „na kawałki”) — przeglądarka nie zna długości pliku z góry. Pliki mp3 z ElevenLabs nie mają nagłówka z czasem trwania, więc długość jest wtedy zgadywana.' });
  if (p.acceptRanges !== 'bytes') out.push({ ok: false, text: 'Serwer nie obsługuje Range (przewijania) — Safari potrafi wtedy źle odtwarzać audio.' });
  if (p.decodedSec === null) {
    out.push({ ok: false, text: 'Przeglądarka nie zdekodowała pliku mp3 (decodeAudioData).' });
  } else {
    out.push({ ok: null, text: `Dźwięku w pliku: ${f(p.decodedSec)} (po pełnym zdekodowaniu).` });
  }
  if (p.elementDurationSec !== null) out.push({ ok: null, text: `Element <audio> podaje długość: ${f(p.elementDurationSec)}.` });
  if (p.problem) out.push({ ok: false, text: `Odtwarzanie: ${p.problem}` });
  if (p.endedAtSec !== null && p.decodedSec !== null) {
    const lost = p.decodedSec - p.endedAtSec;
    if (lost > TOL) out.push({ ok: false, text: `UCIĘTE przez przeglądarkę: odtwarzanie skończyło się na ${f(p.endedAtSec)}, a w pliku jest ${f(p.decodedSec)} — brakuje ${f(lost)}.` });
    else out.push({ ok: true, text: `Przeglądarka odtworzyła nagranie do końca (${f(p.endedAtSec)} z ${f(p.decodedSec)}).` });
  }
  if (p.elementDurationSec !== null && p.decodedSec !== null && p.decodedSec - p.elementDurationSec > TOL) {
    out.push({ ok: false, text: `Element <audio> uważa, że nagranie trwa ${f(p.elementDurationSec)}, a trwa ${f(p.decodedSec)} — źle odgadnięta długość pliku.` });
  }
  return out;
}

export interface AudioEvent {
  /** Czas zdarzenia (ms od 1970). */
  at: number;
  what: 'play' | 'ended' | 'cut' | 'error' | 'blocked' | 'fallback';
  id: string;
  /** Pozycja odtwarzania i długość w chwili zdarzenia (s). */
  cur?: number;
  dur?: number;
  why?: string;
}

/** Jedna linijka dziennika, np. „+2,31 s · przerwane s.d1s4 po 0,90 z 1,50 s — zastąpione przez b.p3”. */
export function formatAudioEvent(e: AudioEvent, t0: number): string {
  const at = `+${((e.at - t0) / 1000).toFixed(2).replace('.', ',')} s`;
  const pos = e.cur !== undefined ? ` po ${e.cur.toFixed(2).replace('.', ',')}${e.dur !== undefined ? ` z ${e.dur.toFixed(2).replace('.', ',')}` : ''} s` : '';
  const why = e.why ? ` — ${e.why}` : '';
  const label = { play: 'start', ended: 'koniec', cut: 'PRZERWANE', error: 'błąd pliku', blocked: 'zablokowane przez przeglądarkę', fallback: 'głos zapasowy' }[e.what];
  return `${at} · ${label} ${e.id}${pos}${why}`;
}
